// components/SimpleHealthManaBar.tsx

import React from 'react';
import './SimpleHealthManaBar.css';
import { ResourceType } from '../../../../../gameTypes';

interface ResourceDisplay {
  type: ResourceType;      // e.g. "Mana", "Energy", "None"
  current: number;         // real-time resource
  max: number;
}

interface HealthManaBarProps {
  currentHealth: number;
  maxHealth: number;
  shield: number;
  magicShield: number;
  physicalShield: number;

  primaryResource?: ResourceDisplay; 
  // if you want a secondaryResource as well, add something like:
  // secondaryResource?: ResourceDisplay;
}

function getResourceColor(type: ResourceType): string {
  switch (type) {
    case "Mana":
      return "#060684"; // your old resource color
    case "Energy":
      return "#e1c600"; // a yellowish color
    case "Fury":
      return "#9b0000"; // a red color
    case "Flow":
      return "#ffffff"; // or something
    case "Shield":
      return "#999999"; 
    default:
      return "#555555"; // fallback
  }
}

const SimpleHealthManaBar: React.FC<HealthManaBarProps> = ({
  currentHealth,
  maxHealth,
  shield,
  magicShield,
  physicalShield,
  primaryResource
}) => {

  // 1) Health percent
  const healthPercent = maxHealth ? (currentHealth / maxHealth) * 100 : 0;

  // 2) Shield percentages
  const shieldPercent = maxHealth ? (shield / maxHealth) * 100 : 0;
  const magicShieldPercent = maxHealth ? (magicShield / maxHealth) * 100 : 0;
  const physicalShieldPercent = maxHealth ? (physicalShield / maxHealth) * 100 : 0;

  // Prepare shield bars
  const shields = [];
  let cumulativeShieldPercent = 0;

  if (physicalShield > 0) {
    cumulativeShieldPercent += physicalShieldPercent;
    shields.push({
      className: 'physical-shield-fill',
      widthPercent: physicalShieldPercent,
      leftPercent: 100 - cumulativeShieldPercent,
    });
  }
  if (magicShield > 0) {
    cumulativeShieldPercent += magicShieldPercent;
    shields.push({
      className: 'magic-shield-fill',
      widthPercent: magicShieldPercent,
      leftPercent: 100 - cumulativeShieldPercent,
    });
  }
  if (shield > 0) {
    cumulativeShieldPercent += shieldPercent;
    shields.push({
      className: 'shield-fill',
      widthPercent: shieldPercent,
      leftPercent: 100 - cumulativeShieldPercent,
    });
  }

  // 3) Resource bar logic
  let resourceBar = null;
  if (primaryResource && primaryResource.type !== "None") {
    const resourcePercent = primaryResource.max 
      ? (primaryResource.current / primaryResource.max) * 100 
      : 0;
    const resourceColor = getResourceColor(primaryResource.type);

    resourceBar = (
      <div className="resource-bar-hmb">
        <div className="bar-hmb">
          <div
            className="fill-hmb resource-fill-hmb"
            style={{
              width: `${resourcePercent}%`,
              backgroundColor: resourceColor
            }}
          ></div>
        </div>
      </div>
    );
  }

  //console.log("Primary resource:", primaryResource);

  return (
    <div className="champion-health-mana-bar">
      {/* Health Bar */}
      <div className="health-bar-hmb">
        <div className="bar-hmb">
          {/* Health fill */}
          <div
            className="fill-hmb health-fill-hmb"
            style={{ width: `${healthPercent}%` }}
          ></div>

          {/* Shield overlays */}
          {shields.length > 0 && (
            <div className="shield-overlays">
              {shields.map((shieldData, index) => (
                <div
                  key={index}
                  className={`shield-fill-hmb ${shieldData.className}`}
                  style={{
                    width: `${shieldData.widthPercent}%`,
                    left: `${shieldData.leftPercent}%`,
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resource Bar (Only if we actually have a resource) */}
      {resourceBar}
    </div>
  );
};

export default SimpleHealthManaBar;
